import {
  Button,
  ButtonColor,
  ButtonSize,
  Combobox,
  Modal,
  ModalType,
  Option,
  Table,
  TableRow,
  TextInput,
} from '@code/innovance-react-ui-kit';
import React, { FC, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Document, PlasticCardRegistrationApi } from 'src/front/api/apiInstance';
import {
  ModelError,
  PlasticCardAdditionalInfoComponentsData,
  ReferenceDataType,
  UploadType,
} from 'src/front/api/generated';
import usePRM, { useConditionalPRM } from 'src/front/common/hooks/usePRM';
import useYupResolver from 'src/front/common/hooks/useYupResolver';
import { formatDate } from 'src/front/common/utils/dateTimeUtils';
import {
  dictionaryLineFormat,
  formatAlphaNumericCyrillicSpecialCharacters,
  formatNumeric,
  optionFormat,
} from 'src/front/common/utils/formUtils';
import preview from 'src/front/common/utils/previewUtils';
import { PlasticCardRegistrationSchema } from 'src/front/common/utils/validationSchemas';
import AsyncButton from 'src/front/components/AsyncButton';
import Datepicker from 'src/front/components/Datepicker';
import {
  ButtonRow,
  Container,
  Form,
  FormElement,
  FormRow,
  InformationSubTitle,
  LineFormElement,
  PlasticCardRow,
  Separator,
  Subtitle,
  TableContainer,
  TableWrapper,
  Title,
} from 'src/front/components/FormStyles';
import PlasticCardDocumentUploadForm, {
  PlasticCardDocumentUploadFormData,
} from 'src/front/components/PlasticCardDocumentUploadForm';
import { useUserInfoState } from 'src/front/contexts/SessionContext';
import Accounts from './Accounts';

import OperationsPerformed from './OperationsPerformed';

const PLASTIC_CARD_DOCUMENT_TYPE = 'AD';

export interface PlasticCardPageData {
  pointOfSale: Option | null;
  operations: Option | null;
  accountType: Option | null;
  quantity: string;
  actDocumentCreateDate: Date | null;
  actNumber: string;
  actNumberCombo: Option | null;
  fromTheAccount: Option | null;
  toTheAccount: Option | null;
  branchCode: Option | null;
  toPointOfSale: Option | null;
}

const defaultPageValues = {
  pointOfSale: null,
  operations: null,
  accountType: null,
  quantity: '',
  actDocumentCreateDate: null,
  actNumber: '',
  actNumberCombo: null,
  fromTheAccount: null,
  toTheAccount: null,
  branchCode: null,
  toPointOfSale: null,
};

const documentTableHeaders = [
  'plasticCardRegistration_documentNote_label',
  'plasticCardRegistration_documentNumber_label',
];

const PlasticCardRegistration: FC = () => {
  const { t } = useTranslation();

  const resolver = useYupResolver(PlasticCardRegistrationSchema, { todayMaxControl: true });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [components, setComponents] = useState<PlasticCardAdditionalInfoComponentsData[]>();
  const [actNumberComboData, setactNumberComboData] = useState<Option[]>([]);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [showOperationPerformed, setShowOperationPerformed] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [documentFormValues, setDocumentFormValues] = useState<PlasticCardDocumentUploadFormData>();
  const [selectedDocument, setSelectedDocument] = useState<TableRow>();
  const [documentList, setDocumentList] = useState<PlasticCardDocumentUploadFormData[]>([]);
  const [selectedComponent, setSelectedComponent] = useState<ReactElement>();
  const [modalStatus, setModalStatus] = useState<boolean>(false);
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
  const [uploadError, setUploadError] = useState<ModelError>();
  const [page, setPage] = useState<number>(1);
  const pointOfSaleData = usePRM(ReferenceDataType.PLASTICCARDPOINTOFSALETYPE);
  const operationsData = usePRM(ReferenceDataType.PLASTICCARDOPERATIONSTYPE);
  const branchCodeData = usePRM(ReferenceDataType.APPLICATIONBRANCH);
  const toPointOfSaleData = usePRM(ReferenceDataType.PLASTICCARDADDITIONALTOPOINTOFSALETYPE);

  const { processDate } = useUserInfoState();
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const dataLimit = 5;

  useEffect(() => {
    components?.find(
      (item) => item.componentValue === 'plasticCardRegistration_actDocumentCreateDate_label'
    )
      ? setSelectedAreas([...selectedAreas, 'actDocumentCreateDate'])
      : setSelectedAreas([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [components]);

  useEffect(() => {
    setHasNextPage(documentList.length > dataLimit);
  }, [documentList.length]);

  const { errors, register, setValue, watch, formState } = useForm<PlasticCardPageData>({
    defaultValues: {
      ...defaultPageValues,
      actDocumentCreateDate: processDate || null,
    },
    resolver,
  });

  const accountTypeData = useConditionalPRM(
    ReferenceDataType.PLASTICCARDACCOUNTTYPE,
    watch('operations')?.value
  );

  const fromTheAccountData = useConditionalPRM(
    ReferenceDataType.PLASTICCARDADDITIONALACCOUNTTYPE,
    watch('accountType')?.value
  );

  useEffect(() => {
    setValue('fromTheAccount', null);
    setValue('toTheAccount', null);
    setValue('actNumberCombo', null);
  }, [fromTheAccountData, setValue]);

  const areaControl = (fieldName: string): void => {
    const isSameArea = selectedAreas.filter((item) => item === fieldName);
    isSameArea?.length > 0
      ? setSelectedAreas([...selectedAreas])
      : setSelectedAreas([...selectedAreas, fieldName]);
  };

  const fullActNumberCombo = async (): Promise<void> => {
    const { data } = await PlasticCardRegistrationApi.getActNumberCombo({
      pointOfSale: watch('pointOfSale')?.value || '',
      accountType: watch('accountType')?.value || '',
    });

    setactNumberComboData(data.parameterDataList.map(optionFormat));
  };

  const clearValues = (fieldName: string): void => {
    switch (fieldName) {
      case 'pointOfSale':
        setValue('operations', null);
        setValue('accountType', null);
        break;
      case 'allFields':
        setValue('accountType', null);
        setValue('quantity', '');
        setValue('actDocumentCreateDate', processDate || null);
        setValue('actNumber', '');
        setValue('actNumberCombo', null);
        setValue('fromTheAccount', null);
        setValue('toTheAccount', null);
        setValue('branchCode', null);
        setValue('toPointOfSale', null);
        break;
      default:
        break;
    }
  };

  const handleComboChange = (fieldName: string) => async (option: Option | null): Promise<void> => {
    setValue(fieldName, option, { shouldValidate: true, shouldDirty: true });

    switch (fieldName) {
      case 'pointOfSale':
        clearValues('pointOfSale');
        break;
      case 'operations':
        getAdditionalInfoComponent();
        setSelectedAreas([]);
        clearValues('allFields');
        break;
      case 'accountType':
        if (watch('operations')?.value === '19') {
          fullActNumberCombo();
        } else if (watch('operations')?.value === '20') {
          const findHeadOffice = branchCodeData.find((item) => item.value === '00009') || null;
          setValue('branchCode', findHeadOffice);
          setSelectedAreas([...selectedAreas, 'branchCode']);
        }
        break;
      default:
        break;
    }

    if (fieldName !== 'pointOfSale' && fieldName !== 'operations' && fieldName !== 'accountType') {
      option?.value
        ? areaControl(fieldName)
        : setSelectedAreas(selectedAreas.filter((item) => item !== fieldName));
    }
  };

  const handleTextChange = (fieldName: string) => async (option: string) => {
    setValue(fieldName, option, { shouldValidate: true, shouldDirty: true });

    if (option) {
      areaControl(fieldName);
    } else {
      setSelectedAreas(selectedAreas.filter((item) => item !== fieldName));
    }
  };

  const handleDateChange = (fieldName: string) => (date: Date): void => {
    setValue(fieldName, date, {
      shouldValidate: formState.isSubmitted || !!date,
      shouldDirty: formState.isSubmitted || !!date,
    });

    if (date) {
      areaControl(fieldName);
    } else {
      setSelectedAreas(selectedAreas.filter((item) => item !== fieldName));
    }
  };

  const handleOperationPerformed = (compName: string): void => {
    setShowOperationPerformed(true);

    compName === 'OperationsPerformed'
      ? setSelectedComponent(
          <OperationsPerformed
            pointOfSaleData={pointOfSaleData}
            setShowOperationPerformed={setShowOperationPerformed}
          />
        )
      : setSelectedComponent(
          <Accounts pointOfSaleData={pointOfSaleData} setShowAccounts={setShowOperationPerformed} />
        );
  };

  const getAdditionalInfoComponent = async (): Promise<void> => {
    if (watch('operations')) {
      const { data } = await PlasticCardRegistrationApi.getAdditionalInfoComponents({
        operations: watch('operations')?.value || '',
      });

      if (data.result === 'OK') {
        setComponents(data.componentsList);
      }
    }
  };

  const registerFields = useCallback(() => {
    register({ name: 'pointOfSale' });
    register({ name: 'operations' });
    register({ name: 'accountType' });
    register({ name: 'quantity' });
    register({ name: 'actDocumentCreateDate' });
    register({ name: 'actNumber' });
    register({ name: 'actNumberCombo' });
    register({ name: 'fromTheAccount' });
    register({ name: 'toTheAccount' });
    register({ name: 'branchCode' });
    register({ name: 'toPointOfSale' });
    register({ name: 'files' });
    register({ name: 'filePath' });
  }, [register]);

  useEffect(() => {
    registerFields();
  }, [registerFields]);

  const addDocument = useCallback((): void => {
    setDocumentFormValues(undefined);
    setIsModalVisible(true);
  }, []);

  const editDocument = useCallback(
    (item?: TableRow): void => {
      setDocumentFormValues(
        documentList.find((doc) => {
          return doc.documentId === item?.id;
        })
      );
      setIsModalVisible(true);
    },
    [documentList]
  );

  const previewDocument = useCallback(
    (item?: TableRow): void => {
      preview(documentList, item);
    },
    [documentList]
  );

  const deleteDocument = useCallback((item?: TableRow): void => {
    setIsDeleteModalOpen(true);
  }, []);

  const actions = useMemo(
    () => ({
      add: addDocument,
      edit: editDocument,
      delete: deleteDocument,
      preview: previewDocument,
    }),
    [addDocument, deleteDocument, editDocument, previewDocument]
  );

  const handleDeleteIdentity = async (): Promise<void> => {
    const { data } = await Document.deleteDocument({
      documentId: selectedDocument?.id,
      uploadType: UploadType.PLASTICCARD,
    });

    if (data.result === 'OK') {
      setDocumentList(documentList.filter((doc) => doc.documentId !== selectedDocument?.id));
      setSelectedDocument(undefined);
      setIsDeleteModalOpen(false);
    }
  };

  const handleContinue = async (): Promise<void> => {
    try {
      const { data } = await PlasticCardRegistrationApi.generatePlasticCardApplication({
        pointOfSale: watch('pointOfSale')?.value || '',
        operations: watch('operations')?.value || '',
        accountType: watch('accountType')?.value || '',
        quantity: watch('quantity') || '',
        documentCreateDate: formatDate(watch('actDocumentCreateDate')) || '',
        actNumber: watch('actNumber') ? watch('actNumber') : watch('actNumberCombo')?.label,
        fromAccount: watch('fromTheAccount')?.value || '',
        branchCode: watch('branchCode')?.value || '',
        toPointOfSale: dictionaryLineFormat(watch('toPointOfSale')),
        actNumberCombo: watch('actNumberCombo')?.value || '',
        toAccount: watch('toTheAccount')?.value || '',
        documentList: documentList.map((document) => ({
          ...document,
          documentType: dictionaryLineFormat({
            label: PLASTIC_CARD_DOCUMENT_TYPE,
            value: PLASTIC_CARD_DOCUMENT_TYPE,
          }),
        })),
      });

      if (data.result === 'OK') {
        setModalStatus(true);
      }
    } catch (err) {
      // TODO (mertohancan): show error on modal or handle with error boundary
    }
  };

  const paginationDataList = documentList.slice((page - 1) * dataLimit, page * dataLimit);

  const documentTableData: TableRow[] = paginationDataList.map(
    (document: PlasticCardDocumentUploadFormData): TableRow => {
      return {
        id: document.documentId,
        columns: [document.explanation, document.documentId],
        deletable: !document.isOldDocument,
        editable: !document.isOldDocument,
        previewable: !document.isOldDocument,
      };
    }
  );

  const onSubmit = async (formData: PlasticCardDocumentUploadFormData): Promise<void> => {
    setUploadError(undefined);

    let list;
    try {
      setIsLoading(true);
      if (!formData.documentId) {
        const { data } = await Document.uploadDocuments(
          formData.files && formData.files[0],
          PLASTIC_CARD_DOCUMENT_TYPE,
          UploadType.PLASTICCARD,
          formData.explanation
        );

        if (data.result === 'OK') {
          list = [
            {
              explanation: data.explanation,
              isOldDocument: data.isOldDocument,
              documentId: data.documentId,
              createDate: data.createDate,
              filePath: formData.filePath,
              documentName: data.documentName,
            },
            ...documentList,
          ];
          setIsModalVisible(false);
        }
      } else {
        const { data } = await Document.updateDocument({
          documentId: formData.documentId,
          explanation: formData.explanation,
          documentType: PLASTIC_CARD_DOCUMENT_TYPE,
          documentName: formData.documentName,
          uploadType: UploadType.PLASTICCARD,
        });

        if (data.result === 'OK') {
          list = documentList.map((element) => {
            if (element.documentId === data.documentId) {
              return {
                explanation: data.explanation,
                isOldDocument: data.isOldDocument,
                documentId: data.documentId,
                createDate: data.createDate,
                filePath: formData.filePath,
                documentName: data.documentName,
              };
            }
            return element;
          });
          setIsModalVisible(false);
        }
      }

      setDocumentList(list || []);
      setIsLoading(false);
    } catch (error) {
      setUploadError(error);
    }
  };

  const closeModal = (): void => {
    setUploadError(undefined);
    setIsModalVisible(false);
    setIsLoading(false);
  };

  const addDocumentModal = (): void => {
    setIsModalVisible(true);
    setDocumentFormValues(undefined);
  };

  const processComplete = (): void => {
    setModalStatus(false);
    setValue('pointOfSale', null);
    setValue('operations', null);
    clearValues('allFields');
    setDocumentList([]);
    setSelectedAreas([]);
  };

  const informationText = (topPadding?: string): JSX.Element => (
    <InformationSubTitle topPadding={topPadding}>
      <Trans
        i18nKey={t('plasticCardRegistration_posNotification_text', {
          pointOfSale: watch('pointOfSale')?.label,
        })}
      />
    </InformationSubTitle>
  );

  const getNextComponents = (): (JSX.Element | null)[] => {
    const visibleComponents = components?.filter((item) => item.isVisible === true) || [];
    const informationComponent = visibleComponents.length < 5 ? informationText('10px') : null;

    const elements = components?.map((item) => {
      if (item.isVisible) {
        switch (item.componentValue) {
          case 'plasticCardRegistration_quantity_label':
            return (
              <FormElement>
                <TextInput
                  errorMessage={t(errors?.quantity?.message as string)}
                  formatter={formatNumeric}
                  label={t('plasticCardRegistration_quantity_label')}
                  name="quantity"
                  onChange={handleTextChange('quantity')}
                  value={watch('quantity') !== '0' ? watch('quantity') : ''}
                  required
                />
              </FormElement>
            );
          case 'plasticCardRegistration_actDocumentCreateDate_label':
            return (
              <FormElement>
                <Datepicker
                  errorMessage={t(errors?.actDocumentCreateDate?.message as string)}
                  inputLabel={`${t('plasticCardRegistration_actDocumentCreateDate_label')} *`}
                  onChange={handleDateChange('actDocumentCreateDate')}
                  value={watch('actDocumentCreateDate') as Date}
                />
              </FormElement>
            );
          case 'plasticCardRegistration_actNumber_label':
            return (
              <FormElement>
                {watch('operations')?.value === '19' ? (
                  <Combobox
                    errorMessage={t(errors?.actNumberCombo?.message as string)}
                    items={actNumberComboData}
                    label={t('plasticCardRegistration_actNumber_label')}
                    name="actNumberCombo"
                    onChange={handleComboChange('actNumberCombo')}
                    searchPlaceholder={t('plasticCardRegistration_searchActNumber_text')}
                    value={watch('actNumberCombo')}
                    disallowDefaultValue
                    searchable
                  />
                ) : (
                  <TextInput
                    errorMessage={t(errors?.actNumber?.message as string)}
                    formatter={formatAlphaNumericCyrillicSpecialCharacters}
                    label={t('plasticCardRegistration_actNumber_label')}
                    name="actNumber"
                    onChange={handleTextChange('actNumber')}
                    value={watch('actNumber')}
                  />
                )}
              </FormElement>
            );
          case 'plasticCardRegistration_fromTheAccount_label':
            return (
              <FormElement>
                <Combobox
                  errorMessage={t(errors?.fromTheAccount?.message as string)}
                  items={fromTheAccountData}
                  label={t('plasticCardRegistration_fromTheAccount_label')}
                  name="fromTheAccount"
                  onChange={handleComboChange('fromTheAccount')}
                  value={watch('fromTheAccount')}
                  disallowDefaultValue
                />{' '}
                {informationComponent}
              </FormElement>
            );
          case 'plasticCardRegistration_toTheAccount_label':
            return (
              <FormElement>
                <Combobox
                  errorMessage={t(errors?.toTheAccount?.message as string)}
                  items={fromTheAccountData}
                  label={t('plasticCardRegistration_toTheAccount_label')}
                  name="toTheAccount"
                  onChange={handleComboChange('toTheAccount')}
                  value={watch('toTheAccount')}
                  disallowDefaultValue
                />
                {informationComponent}
              </FormElement>
            );
          case 'plasticCardRegistration_branchCode_label':
            return (
              <>
                <FormElement>
                  <Combobox
                    errorMessage={t(errors?.branchCode?.message as string)}
                    items={branchCodeData}
                    label={t('plasticCardRegistration_branchCode_label')}
                    name="branchCode"
                    onChange={handleComboChange('branchCode')}
                    searchPlaceholder={t('plasticCardRegistration_searchBranch_text')}
                    value={watch('branchCode')}
                    disallowDefaultValue
                    searchable
                  />
                </FormElement>
                <FormElement>{informationText()}</FormElement>
              </>
            );
          case 'plasticCardRegistration_toPointOfSale_label':
            return (
              <>
                <FormElement>
                  <Combobox
                    errorMessage={t(errors?.toPointOfSale?.message as string)}
                    items={toPointOfSaleData}
                    label={t('plasticCardRegistration_toPointOfSale_label')}
                    name="toPointOfSale"
                    onChange={handleComboChange('toPointOfSale')}
                    searchPlaceholder={t('plasticCardRegistration_searchPointOfSale_text')}
                    value={watch('toPointOfSale')}
                    disallowDefaultValue
                    searchable
                  />
                </FormElement>
                <FormElement>{informationText()}</FormElement>
              </>
            );
          default:
            return null;
        }
      }
      return null;
    });
    return elements || [];
  };

  const loadItems = useCallback(
    async (pageNumber?: number): Promise<void> => {
      setPage(pageNumber || page);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setPage]
  );

  return (
    <Container>
      {!showOperationPerformed ? (
        <>
          <ButtonRow>
            <Button
              color={ButtonColor.Primary}
              onClick={() => handleOperationPerformed('OperationsPerformed')}
              size={ButtonSize.Medium}
              text={t('plasticCardRegistration_operationsPerformed_button')}
              type="button"
            />
          </ButtonRow>
          <ButtonRow>
            <Button
              color={ButtonColor.Primary}
              onClick={() => handleOperationPerformed('Accounts')}
              size={ButtonSize.Medium}
              text={t('plasticCardRegistration_account_button')}
              type="button"
            />
          </ButtonRow>
          <Title>
            <Trans i18nKey="plasticCardRegistration_title" />
          </Title>
          <Subtitle>
            <Trans i18nKey="general_components_basicInfo_text" />
          </Subtitle>
          <Form>
            <FormRow>
              <LineFormElement>
                <Combobox
                  errorMessage={t(errors?.pointOfSale?.message as string)}
                  items={pointOfSaleData}
                  label={t('plasticCardRegistration_pointOfSale_label')}
                  name="pointOfSale"
                  onChange={handleComboChange('pointOfSale')}
                  value={watch('pointOfSale')}
                  required
                />
              </LineFormElement>
            </FormRow>
            {watch('pointOfSale')?.value ? (
              <FormRow>
                <LineFormElement>
                  <Combobox
                    errorMessage={t(errors?.operations?.message as string)}
                    items={operationsData}
                    label={t('plasticCardRegistration_operations_label')}
                    name="operations"
                    onChange={handleComboChange('operations')}
                    value={watch('operations')}
                    required
                  />
                </LineFormElement>
              </FormRow>
            ) : null}
            {watch('operations')?.value && watch('pointOfSale')?.value ? (
              <FormRow>
                <LineFormElement>
                  <Combobox
                    errorMessage={t(errors?.accountType?.message as string)}
                    items={accountTypeData}
                    label={t('general_components_accountType_label')}
                    name="accountType"
                    onChange={handleComboChange('accountType')}
                    value={watch('accountType')}
                    required
                  />
                </LineFormElement>
              </FormRow>
            ) : null}
            {components && watch('accountType')?.value ? (
              <>
                <Separator />
                <Subtitle>
                  <Trans i18nKey="general_components_additionalInfo_text" />
                </Subtitle>

                <PlasticCardRow>{getNextComponents()}</PlasticCardRow>
              </>
            ) : null}
            <Separator />
            {components?.length === selectedAreas.length ? (
              <>
                {watch('pointOfSale')?.value && watch('accountType')?.value ? (
                  <>
                    <Subtitle>
                      <Trans i18nKey="plasticCardRegistration_uploadedDocuments_text" />
                    </Subtitle>
                    {documentList.length > 0 ? null : (
                      <>
                        <Separator />
                        <FormElement>
                          <Button
                            color={ButtonColor.Secondary}
                            onClick={addDocumentModal}
                            size={ButtonSize.Medium}
                            text={t('general_add_button')}
                            type="button"
                          />
                        </FormElement>
                      </>
                    )}
                  </>
                ) : null}

                {isModalVisible ? (
                  <PlasticCardDocumentUploadForm
                    error={uploadError}
                    formValues={documentFormValues}
                    onClose={closeModal}
                    submit={onSubmit}
                  />
                ) : null}
                {isDeleteModalOpen ? (
                  <>
                    <Modal
                      cancelText={t('general_cancel_button')}
                      description={t('general_deleteRecordConfirm_text')}
                      onCancel={() => setIsDeleteModalOpen((state) => !state)}
                      onSubmit={handleDeleteIdentity}
                      submitText={t('general_components_confirm_button')}
                      title="Confirmation"
                      type={ModalType.Confirmation}
                    />
                  </>
                ) : null}
                {documentList.length > 0 &&
                watch('pointOfSale')?.value &&
                watch('accountType')?.value ? (
                  <>
                    <TableWrapper>
                      <TableContainer>
                        <Table
                          actionConfig={actions}
                          addActionText={t('general_add_button')}
                          emptyTableText={t('cardEntry_documentEntry_noUploadedDocumentFound_text')}
                          hasNextPage={hasNextPage}
                          headers={documentTableHeaders.map((head) => t(head))}
                          items={documentTableData}
                          loading={isLoading}
                          loadPageItems={loadItems}
                          onSelect={setSelectedDocument}
                          pageNumber={page}
                          selectedItem={selectedDocument}
                          widths={['60', '30']}
                        />
                      </TableContainer>
                    </TableWrapper>
                  </>
                ) : null}
                {modalStatus ? (
                  <>
                    <Modal
                      description={t('plasticCardRegistration_success_text')}
                      onCancel={processComplete}
                      submitText={t('general_components_ok_button')}
                      title=""
                      type={ModalType.Success}
                    />
                  </>
                ) : null}
              </>
            ) : null}
          </Form>
          {documentList.length > 0 && watch('pointOfSale')?.value && watch('accountType')?.value ? (
            <AsyncButton
              color={ButtonColor.Primary}
              onClick={handleContinue}
              size={ButtonSize.Medium}
              text={t('general_continue_button')}
              type="submit"
            />
          ) : null}
        </>
      ) : (
        selectedComponent
      )}
    </Container>
  );
};
export default PlasticCardRegistration;
